import { NgOptimizedImage } from '@angular/common';
import { Component, inject, signal, OnInit, OnDestroy } from '@angular/core';
import { SPACE_CARD_IMAGE_SIZES } from '../../shared/storage/space-image.constants';
import { Router, RouterLink } from '@angular/router';
import { Auth, authState } from '@angular/fire/auth';
import { take } from 'rxjs/operators';
import { LucideAngularModule, LucideIconProvider, LUCIDE_ICONS, icons } from 'lucide-angular';
import { HeroCardComponent } from '../hero-card/hero-card.component';
import { SiteFooterComponent } from '../site-footer/site-footer.component';
import { AssetService } from '../../services/asset.service';
import { minHourlyRate, spaceCoverThumb, Space, SPACE_CATEGORY_LABELS, getSpaceCategories } from '../../models/asset.model';
import { spaceLocationLabel as resolveSpaceLocationLabel } from '../../shared/location/address-resolution.utils';
import { spacePublicPathCommands } from '../../shared/location/space-public-url.utils';
import { SeoService } from '../../services/seo.service';
import { truncateSeoText } from '../../shared/seo/seo.utils';

export interface OwnerBenefit {
  icon: string;
  title: string;
  desc: string;
  tag: string;
}

export interface OwnerFaq {
  q: string;
  a: string;
}

export interface LandingTestimonial {
  quote: string;
  name: string;
  role: string;
  initials: string;
}

@Component({
  selector: 'app-landing-owner',
  imports: [RouterLink, NgOptimizedImage, HeroCardComponent, SiteFooterComponent, LucideAngularModule],
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }],
  templateUrl: './landing-owner.component.html',
})
export class LandingOwnerComponent implements OnInit, OnDestroy {
  readonly cardImageSizes = SPACE_CARD_IMAGE_SIZES;

  private auth         = inject(Auth);
  private router       = inject(Router);
  private assetService = inject(AssetService);
  private seo          = inject(SeoService);

  readonly latestSpaces  = signal<Space[]>([]);
  readonly spacesLoading = signal(true);

  readonly ownerBenefits = signal<OwnerBenefit[]>([
    {
      icon:  'calendar-days',
      title: 'Deja de gestionar. Empieza a cobrar.',
      desc:  'Tu calendario se actualiza solo con cada reserva confirmada. El contrato digital se firma automáticamente antes de que el cliente entre. Sin llamadas, sin papeles, sin sorpresas.',
      tag:   'Gestión automática',
    },
    {
      icon:  'credit-card',
      title: 'El dinero llega antes que el cliente.',
      desc:  'Cada reserva se paga al 100% con tarjeta en el momento de confirmarla. Tú recibes tus ganancias netas de forma automática. Se acabó perseguir Bizums, transferencias pendientes o excusas.',
      tag:   'Cobro seguro',
    },
    {
      icon:  'shield-check',
      title: 'Si tú no ganas, nosotros tampoco.',
      desc:  'Sin cuotas fijas. Sin suscripciones. Solo cobramos una comisión cuando te conseguimos una reserva real. Tú pones tu precio. Nosotros ponemos la tecnología. Sin letra pequeña.',
      tag:   'Riesgo cero',
    },
  ]);

  readonly ownerFaqs = signal<OwnerFaq[]>([
    {
      q: '¿Cuánto cuesta realmente publicar mi espacio?',
      a: 'Cero euros. El registro, la publicación y todas las herramientas son gratuitas. Solo existe una comisión cuando una reserva real se confirma y cobra (desde el 5% con volumen).',
    },
    {
      q: '¿Qué pasa si una semana no tengo ninguna reserva?',
      a: 'No pasa nada. Literalmente. No hay cuotas, no hay penalizaciones, no hay nada que pagar. Tu espacio sigue publicado sin coste mientras tú decides.',
    },
    {
      q: '¿Cómo y cuándo recibo mis ingresos?',
      a: 'El cliente paga al reservar. Tú recibes tus ganancias netas directamente, de forma automática, una vez completada la reserva. Sin intermediarios manuales, sin esperas arbitrarias.',
    },
  ]);

  readonly testimonials = signal<LandingTestimonial[]>([
    {
      quote:    'Pasamos de gestionar reservas por WhatsApp a tener todo centralizado. El calendario nos ahorra horas cada semana.',
      name:     'María García',
      role:     'Local de eventos, Madrid',
      initials: 'MG',
    },
    {
      quote:    'La claridad del panel y los pagos integrados nos dieron confianza para abrir más franjas horarias sin miedo a solapes.',
      name:     'Carlos Ruiz',
      role:     'Coworking, Valencia',
      initials: 'CR',
    },
    {
      quote:    'Publicar el estudio fue rápido y las reservas llegan con toda la información. Ideal para un negocio pequeño.',
      name:     'Laura Méndez',
      role:     'Estudio creativo, Barcelona',
      initials: 'LM',
    },
  ]);

  ngOnInit(): void {
    this.seo.setPage({
      title: 'Publica tu espacio y recibe reservas | TimeFlexRent',
      description: truncateSeoText(
        'Monetiza tu local por horas con TimeFlexRent. Calendario, pagos online, contratos y gestión de reservas en un solo panel.',
      ),
      canonicalPath: '/propietarios',
    });

    this.assetService.getLatestSpaces(6).then(spaces => {
      this.latestSpaces.set(spaces);
      this.spacesLoading.set(false);
    }).catch(() => this.spacesLoading.set(false));
  }

  ngOnDestroy(): void {
    this.seo.resetToDefault();
  }

  goToCta(): void {
    authState(this.auth).pipe(take(1)).subscribe(user => {
      this.router.navigate([user ? '/mis-espacios' : '/login']);
    });
  }

  spaceLink(space: Space): string[] {
    return spacePublicPathCommands(space);
  }

  spaceLocationLabel(space: Space): string {
    return resolveSpaceLocationLabel(space);
  }

  spaceCategoryLabel(space: Space): string {
    return SPACE_CATEGORY_LABELS[getSpaceCategories(space)[0]];
  }

  hourlyRate(space: Space): number {
    return minHourlyRate(space);
  }

  coverThumb(space: Space): string {
    return spaceCoverThumb(space);
  }
}
